const CONVERSION_STORAGE_KEY = 'conversion_source';
const PREMIUM_PROMPT_STORAGE_KEY = 'last_premium_prompt';
// ponytail: fixed 7-day window, tune if checkout lag data says otherwise
const CONVERSION_MAX_AGE_SECONDS = 7 * 24 * 60 * 60;

export type ConversionAssetType =
  | 'music'
  | 'sfx'
  | 'motion'
  | 'stock_footage'
  | 'lut';

export interface ConversionRecord {
  assetId?: number;
  assetType?: ConversionAssetType;
  path: string;
  source: string;
  timestamp: number;
}

export interface ResolvedConversion {
  conversion_asset_id?: number;
  conversion_asset_type?: ConversionAssetType;
  conversion_path?: string;
  conversion_source: string;
}

interface RecordConversionArgs {
  assetId?: number;
  assetType?: ConversionAssetType;
  source: string;
}

/**
 * Record the origin of a conversion at the moment a CTA is clicked. Captures
 * the current page path so purchase attribution reflects where the journey
 * started, not the post-checkout `/music` landing page. Last-touch: overwrites
 * any previous record.
 */
export const recordConversion = ({
  source,
  assetType,
  assetId,
}: RecordConversionArgs): void => {
  if (!source) {
    return;
  }

  try {
    // Preserved side-effect: some upsell surfaces read the last premium prompt.
    if (source.includes('goPremium')) {
      localStorage.setItem(PREMIUM_PROMPT_STORAGE_KEY, source);
    }

    const record: ConversionRecord = {
      source,
      path: window.location.pathname,
      timestamp: Math.floor(Date.now() / 1000),
    };

    if (assetType) {
      record.assetType = assetType;
    }

    if (typeof assetId === 'number' && !Number.isNaN(assetId)) {
      record.assetId = assetId;
    }

    localStorage.setItem(CONVERSION_STORAGE_KEY, JSON.stringify(record));
  } catch (e) {
    console.error(e);
  }
};

/**
 * Read the current conversion record for inclusion in a purchase event.
 * Tolerates the legacy array-stack shape written before this rework by
 * returning the most recent entry's event name.
 */
export const getConversion = (): ResolvedConversion => {
  try {
    const stored = localStorage.getItem(CONVERSION_STORAGE_KEY);

    if (!stored) {
      return { conversion_source: '' };
    }

    const parsed = JSON.parse(stored) as unknown;

    // Legacy shape: a stack of { conversion_event, ... }. Read the newest.
    if (Array.isArray(parsed)) {
      const latest = parsed[0] as { conversion_event?: string } | undefined;
      return { conversion_source: latest?.conversion_event ?? '' };
    }

    const record = parsed as Partial<ConversionRecord>;

    const isStale =
      typeof record.timestamp === 'number' &&
      Date.now() / 1000 - record.timestamp > CONVERSION_MAX_AGE_SECONDS;

    if (!record.source || isStale) {
      return { conversion_source: '' };
    }

    return {
      conversion_source: record.source,
      conversion_path: record.path,
      conversion_asset_type: record.assetType,
      conversion_asset_id: record.assetId,
    };
  } catch {
    return { conversion_source: '' };
  }
};

/** Clear the stored conversion record (call after a purchase is tracked). */
export const clearConversion = (): void => {
  try {
    localStorage.removeItem(CONVERSION_STORAGE_KEY);
  } catch (e) {
    console.error(e);
  }
};
